"use client";

import { Checkbox, Field, SelectField } from "@/components/tool-kit/primitives";
import { INPUT_MONO, SELECT } from "@/components/tool-kit/styles";
import {
  DECIMAL_PLACES,
  type DecimalPlaces,
} from "@/components/tools/geometry/format";
import { t } from "@/lib/i18n/interpolate";
import type { UnitSetup } from "@/lib/tool-kit/geometry/measurements";
import {
  LENGTH_SYMBOLS,
  LENGTH_UNIT_IDS,
  type LengthUnitId,
} from "@/lib/tool-kit/geometry/units";

export interface MeasurementFieldSpec {
  id: string;
  label: string;
}

/**
 * Number inputs for a shape's measurements. With `mixed` on, each one gets its own unit
 * selector (the unit symbols — cm, ft — are not prose, so they are the same in every
 * language); otherwise one shared unit selector elsewhere covers them all.
 */
export function MeasurementFields({
  idPrefix,
  fields,
  values,
  onValue,
  mixed,
  units,
  onUnit,
  unitAria,
}: {
  idPrefix: string;
  fields: readonly MeasurementFieldSpec[];
  values: Record<string, string>;
  onValue: (id: string, value: string) => void;
  mixed: boolean;
  units: Record<string, LengthUnitId>;
  onUnit: (id: string, unit: LengthUnitId) => void;
  /** "{field} unit" — the accessible name of a per-field unit selector. */
  unitAria: string;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((field) => {
        const inputId = `${idPrefix}-${field.id}`;
        return (
          <Field id={inputId} key={field.id} label={field.label}>
            <div className="flex gap-2">
              <input
                className={INPUT_MONO}
                id={inputId}
                inputMode="decimal"
                onChange={(event) => onValue(field.id, event.target.value)}
                type="text"
                value={values[field.id] ?? ""}
              />
              {mixed ? (
                <>
                  <label className="sr-only" htmlFor={`${inputId}-unit`}>
                    {t(unitAria, { field: field.label })}
                  </label>
                  <select
                    className={`${SELECT} w-24 shrink-0`}
                    id={`${inputId}-unit`}
                    onChange={(event) =>
                      onUnit(field.id, event.target.value as LengthUnitId)
                    }
                    value={units[field.id] ?? "cm"}
                  >
                    {LENGTH_UNIT_IDS.map((unit) => (
                      <option key={unit} value={unit}>
                        {LENGTH_SYMBOLS[unit]}
                      </option>
                    ))}
                  </select>
                </>
              ) : null}
            </div>
          </Field>
        );
      })}
    </div>
  );
}

export interface UnitControlStrings {
  unitLabel: string;
  mixLabel: string;
  resultUnitLabel: string;
  decimalsLabel: string;
  lengthUnits: Record<LengthUnitId, string>;
}

/** The unit selector, the "mix units" switch, the answer unit and the decimal places. */
export function UnitControls({
  idPrefix,
  setup,
  onSetup,
  places,
  onPlaces,
  strings,
}: {
  idPrefix: string;
  setup: UnitSetup;
  onSetup: (next: UnitSetup) => void;
  places: DecimalPlaces;
  onPlaces: (next: DecimalPlaces) => void;
  strings: UnitControlStrings;
}) {
  const unitOptions = LENGTH_UNIT_IDS.map((unit) => ({
    value: unit,
    label: strings.lengthUnits[unit],
  }));
  return (
    <div className="grid items-end gap-4 sm:grid-cols-3">
      {setup.mixed ? (
        <SelectField
          id={`${idPrefix}-result-unit`}
          label={strings.resultUnitLabel}
          onChange={(resultUnit) => onSetup({ ...setup, resultUnit })}
          options={unitOptions}
          value={setup.resultUnit}
        />
      ) : (
        <SelectField
          id={`${idPrefix}-unit`}
          label={strings.unitLabel}
          onChange={(unit) => onSetup({ ...setup, unit })}
          options={unitOptions}
          value={setup.unit}
        />
      )}
      <SelectField
        id={`${idPrefix}-places`}
        label={strings.decimalsLabel}
        onChange={onPlaces}
        options={DECIMAL_PLACES.map((value) => ({ value, label: value }))}
        value={places}
      />
      <div className="pb-2">
        <Checkbox
          checked={setup.mixed}
          label={strings.mixLabel}
          onChange={(mixed) =>
            onSetup({
              ...setup,
              mixed,
              resultUnit: mixed ? setup.unit : setup.resultUnit,
            })
          }
        />
      </div>
    </div>
  );
}
